import { create } from 'zustand';
import { backendClient } from '../services/backendClient';
import { buildAuthApiPath } from '../constants';
import { getWalletAddress } from '../utils/token';
import { normalizeRoleName } from '../utils/roles';
import { normalizeAccountDisplayName } from '../utils/accountName';

export interface BackendWallet {
  address: string;
  name: string;
  role: string;
  dbRole?: string;
  status?: string;
  userStatus?: string;
  chainRoleSupported?: boolean;
  publicKey?: string;
}

/** Per-app localStorage key — same namespace pattern as token.ts */
function walletStorageKey(): string {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const app = path.split('/').filter(Boolean)[0] || 'portal';
  return `kxdex_${app}_backend_wallet`;
}

interface WalletState {
  backendWallets: BackendWallet[];
  currentBackendWallet: BackendWallet | null;

  loadBackendWallets: (userWalletAddress?: string) => Promise<void>;
  setCurrentBackendWallet: (wallet: BackendWallet | null) => void;
}

export const useWalletStore = create<WalletState>((set) => ({
  backendWallets: [],
  currentBackendWallet: null,

  loadBackendWallets: async (userWalletAddress?: string) => {
    try {
      const res = await backendClient.getClient().get(buildAuthApiPath('/auth/wallets/list'));
      const raw = res.data;
      if (!Array.isArray(raw)) {
        console.warn('walletStore: unexpected response type', typeof raw);
        return;
      }
      const wallets: BackendWallet[] = raw.map((w: any) => ({
        ...w,
        name: normalizeAccountDisplayName({
          rawName: w.name,
          walletAddress: w.address,
        }),
        role: normalizeRoleName(w.role || ''),
      }));
      set({ backendWallets: wallets });

      // Priority:
      // 1) logged-in user's wallet
      // 2) per-app saved selection (only when no explicit login wallet is provided)
      // 3) first in list
      let matched: BackendWallet | undefined;

      // 1. Login wallet — the address from JWT / auth flow
      const loginAddress = userWalletAddress || getWalletAddress() || '';
      if (loginAddress) {
        matched = wallets.find(w => w.address.toLowerCase() === loginAddress.toLowerCase());
      }

      // 2. Per-app saved selection (e.g. user switched wallet on Wallets page)
      // Only use this fallback when the caller did not provide the authenticated wallet.
      if (!matched && !userWalletAddress) {
        const savedAddress = localStorage.getItem(walletStorageKey());
        if (savedAddress) {
          matched = wallets.find(w => w.address.toLowerCase() === savedAddress.toLowerCase());
        }
      }

      // 3. First in list
      if (!matched && wallets.length > 0) {
        matched = wallets[0];
      }

      if (matched) {
        set({ currentBackendWallet: matched });
        localStorage.setItem(walletStorageKey(), matched.address);
      }
    } catch (err) {
      console.error('Failed to load backend wallets:', err);
    }
  },

  setCurrentBackendWallet: (wallet) => {
    set({ currentBackendWallet: wallet });
    if (wallet) {
      localStorage.setItem(walletStorageKey(), wallet.address);
    } else {
      localStorage.removeItem(walletStorageKey());
    }
  },
}));
